import { Dispatch, FC, useCallback, useEffect, useState } from 'react';
import { CreateStudyAction, CreateStudyState } from '@/components/Studies/Create/Create.model';
import Button from '@/components/UI/Button';
import { _isEmpty, _notNil } from '@/littledash';
import { useModalAction } from '@/utils/modal';
import { SampleDefinitionFormData, SampleDefinitionIssueAction } from '@/model/SamplePlan.model';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate';
import { FormActions } from '@/components/Studies/Create/Show.utils';
import { SamplePlanTableProvider } from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { SamplePlanTable } from '@/components/Samples/SamplePlanTable/SamplePlanTable';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { TeamGlossary } from '@/model/Glosary.model';
import { RiAlertFill } from 'react-icons/ri';
import {
  useSamplePlanAnimalData,
  useSamplePlanGroupData,
} from '@/components/Studies/Create/Steps/SamplingPlan/SamplePlan.utils';
import { useSamplePlanValidation } from '@/components/Samples/useSamplePlanValidation';
import { SampleDefinitionIssuesModal } from '@/components/Samples/SampleDefinitionIssuesModal/SampleDefinitionIssuesModal';
import ReactModal from 'react-modal';
import { createIssueActionHandler } from './SampleDefinitionActions.utils';

type SamplingPlanStepProps = {
  state: CreateStudyState;
  dispatch: Dispatch<CreateStudyAction>;
};
export const SamplingPlanStep: FC<SamplingPlanStepProps> = ({ state, dispatch }) => {
  const { openModal, closeModal } = useModalAction();
  const { setStepReady } = useStudyCreateStep();

  const { response: sampleMetadataGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'sample_meta',
    },
    options: { invokeOnInit: true, onError: { slug: 'sample-meta-load' } },
  });
  const { response: sampleGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
      sort: 'title',
    },
    options: { invokeOnInit: true, onError: { slug: 'samples-glossary-load' } },
  });

  const groupData = useSamplePlanGroupData();
  const animals = useSamplePlanAnimalData();

  const [displayIssueModal, setDisplayIssueModal] = useState(false);
  const { count: issueCount, issues } = useSamplePlanValidation(
    state.study.sample_definitions,
    sampleGlossaryResponse?.body?.data,
    sampleMetadataGlossaryResponse?.body,
    groupData.groupTargets,
    animals.planned_animals,
    animals.registered_animals
  );

  const disabled =
    _isEmpty(groupData.groupTargets) && _isEmpty(animals.planned_animals) && _isEmpty(animals.registered_animals);

  const onSubmit = useCallback((sample_definitions: Array<SampleDefinitionFormData>) => {
    dispatch({
      type: 'updateStudy',
      data: { ...state.study, sample_definitions },
    });
    closeModal();
    return Promise.resolve();
  }, []);

  // Update step ready state when issues change
  useEffect(() => {
    if (sampleMetadataGlossaryResponse?.type === 'success' && sampleGlossaryResponse?.type === 'success') {
      if (_notNil(issues)) {
        setStepReady(issues.size === 0);
        setDisplayIssueModal(issues.size > 0);
      }
    } else {
      setStepReady((state.study.sample_definitions?.length ?? 0) === 0);
    }
  }, [
    setStepReady,
    setDisplayIssueModal,
    issues,
    state.study.sample_definitions,
    sampleMetadataGlossaryResponse,
    sampleGlossaryResponse,
  ]);

  const openSamplePlanModal = () => {
    openModal('STUDY_CREATE_SAMPLE_PLAN', {
      sample_definitions: state.study.sample_definitions ?? [],
      groups: groupData.groupTargets,
      planned_animals: animals.planned_animals,
      registered_animals: animals.registered_animals,
      onSubmit,
    });
  };

  const handleIssueAction = useCallback(
    (action: SampleDefinitionIssueAction) => {
      const updateCallback = (definitions: Array<SampleDefinitionFormData>) => {
        // Factory sends all definitions when no deleteCallback provided (Redux context)
        dispatch({
          type: 'updateStudy',
          data: { ...state.study, sample_definitions: definitions },
        });
      };

      // No deleteCallback for Redux - factory will send all definitions to updateCallback
      const handler = createIssueActionHandler(updateCallback, openSamplePlanModal);
      handler(action, state.study.sample_definitions ?? []);
    },
    [dispatch, state.study, openSamplePlanModal]
  );
  return (
    <>
      <div className="mb4" data-test-component="SamplingPlanStep" data-test-element="header">
        <h3 className="fw5 f5 pb2" data-testid="sampling-plan-title">
          Sampling plan
        </h3>
        <p data-testid="sampling-plan-description">Specify how samples should be collected from groups or animals.</p>
      </div>
      <div className="w-100 ui-card" data-test-component="SamplingPlanStep" data-test-element="card-container">
        {_isEmpty(state.study.sample_definitions) ? (
          <EmptyState disabled={disabled} onClick={openSamplePlanModal} />
        ) : (
          <>
            <div className="flex flex-row items-center justify-end ma3">
              {_notNil(issues) && issues.size > 0 && (
                <a
                  className="mr2 pointer"
                  data-testid="review-issues-button"
                  onClick={() => setDisplayIssueModal(true)}
                >
                  <span className="flex flex-row items-center justify-center">
                    <RiAlertFill className="red mr1" size={16} />
                    <span>
                      {issueCount} issue
                      {issueCount !== 1 ? 's' : ''}
                    </span>
                  </span>
                </a>
              )}
              <Button outline transparent testId="edit-sampling-plan-button" onClick={openSamplePlanModal}>
                <span data-test-element="edit-button">Edit</span>
              </Button>
            </div>
            <SamplePlanTableProvider
              sample_definitions={state.study.sample_definitions ?? []}
              groups={groupData.groupTargets}
              registered_animals={animals.registered_animals}
              planned_animals={animals.planned_animals}
              metadata_glossary={sampleMetadataGlossaryResponse?.body ?? []}
              sample_glossary={(sampleGlossaryResponse?.body as { data?: Array<TeamGlossary> })?.data ?? []}
            >
              <SamplePlanTable />
            </SamplePlanTableProvider>
          </>
        )}
      </div>
      <FormActions state={state} dispatch={dispatch as () => void} />
      {displayIssueModal && _notNil(issues) && _notNil(state.study.sample_definitions) && issues.size > 0 ? (
        <ReactModal
          isOpen
          className="ui-modal"
          style={{ overlay: { backgroundColor: 'rgba(00,00,00,.5)' }, content: { maxHeight: '90vh' } }}
          ariaHideApp={false}
        >
          <SampleDefinitionIssuesModal
            issues={issues}
            sampleDefinitions={state.study.sample_definitions}
            onAction={handleIssueAction}
            onClose={() => setDisplayIssueModal(false)}
          />
        </ReactModal>
      ) : null}
    </>
  );
};

const EmptyState: FC<{ disabled: boolean; onClick: () => void }> = ({ disabled, onClick }) => (
  <div
    className="br2 bg-near-white tc flex items-center justify-center flex-column pa4 ma4"
    data-test-component="EmptyState"
    data-test-element="container"
  >
    <div className="mw6 center ph4 pa4">
      <h3 className="mb2" data-testid="empty-state-title">
        Add planned sample groups
      </h3>
      <p className="mb3" data-testid="empty-state-description">
        A preview of your treatment groups and any associated metadata will appear here
      </p>
      <Button
        stateless
        className="basier-reg blue ph0 mt2"
        disabled={disabled}
        testId="add-planned-samples-button"
        onClick={onClick}
      >
        <span
          data-tooltip-id="global-tooltip-id"
          data-tooltip-content={disabled ? 'Create at least one group before adding a sampling plan' : undefined}
        >
          Add planned samples
        </span>
      </Button>
    </div>
  </div>
);
